import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontIcon } from '@fluentui/react/lib/Icon';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal
} from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { useLanguage, Lang } from 'contexts/LanguageContext';
import { useSubscription } from 'contexts/SubscriptionContext';
import { mergeStyles } from '@fluentui/react/lib/Styling';

interface MobileNavProps {
  language: Lang;
  pathParts: string[];
  signInClickHandler: (instance: IPublicClientApplication) => void;
  signOutClickHandler: (instance: IPublicClientApplication) => void;
}

const iconClass = mergeStyles({
  fontSize: 18,
  height: 18,
  width: 18,
  margin: "-5px 5px 0 0",
});

export default function MobileNav({ 
  language, 
  pathParts,
  signInClickHandler, 
  signOutClickHandler 
}: MobileNavProps) {
  const { instance, accounts } = useMsal();
  const { subscription } = useSubscription();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const langArr: Lang[] = ["fr", "en"];
  const { onLanguageChange, res } = useLanguage();

  function generateLangLink(newLang: Lang, langArr: Lang[], pathParts: string[]): string {
    if (langArr.includes(pathParts[0] as Lang)) {
      pathParts[0] = newLang; // If the path starts with a language code, replace it
    } else {
      pathParts.unshift(newLang); // If the path doesn't start with a language code, add the new language at the beginning
    }

    return '/' + pathParts.join('/');
  }

  // Get user name for display
  const getUserName = (): string => {
    if (accounts.length === 0) return "";
    
    let userName: string | unknown = (accounts[0] && accounts[0].name) ? accounts[0].name.split(" ")[0] : "";
    
    if (userName === "" || userName === "unknown") {
      userName = (accounts[0] && accounts[0].idTokenClaims) ? accounts[0].idTokenClaims.given_name : "";
    }
    
    return userName as string;
  };

  const userName = getUserName();

  // Close mobile menu when screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const mobileMenu = document.querySelector('.mobile-menu-container');
      const hamburgerButton = document.querySelector('[aria-controls="mobile-nav"]');
      
      if (mobileMenuOpen && mobileMenu && hamburgerButton && 
          !mobileMenu.contains(target) && !hamburgerButton.contains(target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="md:hidden flex items-center ml-4">
        <button
          className="text-white hover:text-gray-300 focus:outline-none focus:text-gray-300 transition duration-150 ease-in-out"
          aria-controls="mobile-nav"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="sr-only">Menu</span>
          <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path fillRule="evenodd" d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"/>
            ) : (
              <path fillRule="evenodd" d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"/>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile navigation */}
      {mobileMenuOpen && (
        <nav className="md:hidden fixed inset-0 bg-gray-800 z-50 mobile-menu-container">
          <div className="h-full overflow-y-auto">
            {/* Header with close button */}
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <div className="text-white font-bold text-lg">Menu</div>
              <button
                onClick={closeMobileMenu}
                className="text-white hover:text-gray-300 focus:outline-none"
              >
                <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"/>
                </svg>
              </button>
            </div>
            <div className="px-4 py-6">
            <ul className="space-y-2">
              {/* Accueil - Recherche*/}
              <li className="border-t border-gray-600 pt-3 mt-3">
                <Link
                  to={res.pagesLinks['search']}
                  className="block py-3 px-4 text-white hover:bg-gray-700 rounded transition duration-150 ease-in-out"
                  reloadDocument={true}>
                  {res.pagesMetadata.search.linkTitle}
                </Link>
              </li>

              {/* Plans et abonnements */}
              <li className="border-t border-gray-600 pt-3 mt-3">
                <Link 
                  to="https://talamus.pro/formations" target="_blank"
                  className="block py-3 px-4 text-white hover:bg-gray-700 rounded transition duration-150 ease-in-out"
                >
                  <FontIcon aria-label="NavigateExternalInline" iconName="NavigateExternalInline" className={iconClass} />
                  {res["Plans et abonnements"]}
                </Link>
              </li>

              {/* User welcome section for authenticated users */}
              <AuthenticatedTemplate>
                <li className="bg-gray-700 rounded-lg mb-3">
                  {/* User greeting header */}
                  <div className="py-3 px-4 text-white font-semibold text-lg border-b border-gray-600">
                    {res.Bonjour} {userName}
                  </div>
                  
                  {/* Primary user actions - emphasized */}
                  <div className="py-2">
                    <Link
                      to={res.pagesLinks['history']}
                      reloadDocument={true}
                      className="flex items-center py-3 px-6 text-white hover:bg-gray-600 transition duration-150 ease-in-out font-medium"
                      onClick={closeMobileMenu}
                    >
                      <svg className="w-5 h-5 mr-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      {res.pagesMetadata.history.linkTitle}
                    </Link>
                    
                    <button
                      onClick={() => {
                        signOutClickHandler(instance);
                        closeMobileMenu();
                      }}
                      className="flex items-center w-full py-3 px-6 text-white hover:bg-red-600 transition duration-150 ease-in-out font-medium"
                    >
                      <svg className="w-5 h-5 mr-3 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                      </svg>
                      {res["Déconnexion"]}
                    </button>
                  </div>
                </li>
              </AuthenticatedTemplate>

              {/* Connection section for unauthenticated users */}
              <UnauthenticatedTemplate>
                <li className="border-t border-gray-700 pt-2">
                  <button
                    onClick={() => {
                      signInClickHandler(instance);
                      closeMobileMenu();
                    }}
                    className="flex items-center w-full py-3 px-4 text-white hover:bg-gray-700 rounded transition duration-150 ease-in-out"
                  >
                    <svg className="w-5 h-5 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {res["Me connecter"]}
                  </button>
                </li>
              </UnauthenticatedTemplate>

              {/* Subscription link - only if no active subscription */}
              <li>
                <Link to={res.pagesLinks['subscribe']} 
                  reloadDocument={true}
                  className="block py-3 px-4 text-white hover:bg-gray-700 rounded transition duration-150 ease-in-out"
                >
                  {res.pagesMetadata.subscribe.linkTitle}
                </Link>
              </li>

              <AuthenticatedTemplate>
                {subscription && !subscription.abonnement ? (
                  <li>
                    <Link
                      to={res.pagesLinks['subscribe']}
                      reloadDocument={true}
                      className="block py-3 px-4 text-white hover:bg-gray-700 rounded transition duration-150 ease-in-out"
                      onClick={closeMobileMenu}
                    >
                      {res.pagesMetadata.subscribe.linkTitle}
                    </Link>
                  </li>
                ) : null}
              </AuthenticatedTemplate>

              {/* Language selector section */}
              <li className="border-t border-gray-600 pt-3 mt-3">
                <div className="py-2 px-4 text-gray-300 font-medium text-sm mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.087 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.723 1.447a1 1 0 11-1.79-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd" />
                  </svg>
                  {res.Langue}
                </div>
                <div className="flex space-x-1 px-4">
                  {langArr.map((linkLg: Lang) => (
                    <Link
                      key={"mobile_lg_" + linkLg}
                      to={generateLangLink(linkLg, langArr, pathParts)}
                      className={`flex-1 py-2 px-3 text-center rounded font-medium transition duration-150 ease-in-out ${
                        linkLg === language 
                          ? 'bg-orange-600 text-white' 
                          : 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                      }`}
                      onClick={() => {
                        if (onLanguageChange) {
                          onLanguageChange(linkLg);
                        }
                        closeMobileMenu();
                      }}
                    >
                      {linkLg.toUpperCase()}
                    </Link>
                  ))}
                </div>
              </li>
            </ul>
          </div>
          </div>
        </nav>
      )}
    </>
  );
}